"use client";

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { GripHorizontal } from "lucide-react";

export type DragExploreCursorProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Hides the system cursor over a drag zone and shows a green
 * "Drag To Explore" badge that follows the pointer (fine pointers only).
 */
export function DragExploreCursor({
  children,
  className,
}: DragExploreCursorProps) {
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [supportsFinePointer, setSupportsFinePointer] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: fine)");
    const update = () => setSupportsFinePointer(mediaQuery.matches);

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  const moveBadge = (clientX: number, clientY: number) => {
    const badge = badgeRef.current;
    if (!badge) return;

    badge.style.transform = `translate3d(${clientX}px, ${clientY}px, 0) translate(-50%, -50%)`;
  };

  const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    if (!supportsFinePointer) return;
    setIsActive(true);
    moveBadge(event.clientX, event.clientY);
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!supportsFinePointer) return;
    moveBadge(event.clientX, event.clientY);
  };

  const handleMouseLeave = () => {
    setIsActive(false);
  };

  return (
    <div
      className={[
        className,
        supportsFinePointer ? "cursor-none **:cursor-none" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {supportsFinePointer ? (
        <div
          ref={badgeRef}
          aria-hidden="true"
          className={[
            "pointer-events-none fixed top-0 left-0 z-9999 flex items-center gap-2 rounded-md bg-[#29361a] px-3 py-2 text-xs font-semibold tracking-[0.12em] text-white uppercase shadow-[0_8px_24px_rgba(0,0,0,0.28)] transition-opacity duration-150",
            isActive ? "opacity-100" : "opacity-0",
          ].join(" ")}
        >
          <GripHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
          Drag To Explore
        </div>
      ) : null}
    </div>
  );
}
